import { useState } from 'react'
import {
  IconButton,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import EventIcon from '@mui/icons-material/Event'
import HistoryIcon from '@mui/icons-material/History'
import { SchedulePattern, ISchedulePattern, WeekPattern } from '../../../../../../../../app/entities/SchedulePattern'
import { ITechnician } from '../../../../../../../../app/entities/Technician'
import { ILocation } from '../../../../../../../../app/entities/Location'
import { getActiveSchedulePattern } from '../../../../../../global/utils/schedule-patterns'
import SchedulePatternHistoryModal from './SchedulePatternHistoryModal'
import styles from '../styles.module.css'

interface SchedulePatternEditorProps {
  techData: ITechnician
  location: ILocation | null
  isExpanded: boolean
  isEditing: boolean
  editingData: {
    effectiveFrom: string
    reason: string
    originalPattern: ISchedulePattern | null
  } | null
  draftPattern: ISchedulePattern | null
  onToggleExpanded: () => void
  onStartEditing: () => void
  onCancelEditing: () => void
  onToggleDayInDraft: (weekNumber: number, dayName: string) => void
  onAddWeekToDraft: () => void
  onRemoveWeekFromDraft: (weekNumber: number) => void
  onUpdateEditingData: (
    field: 'effectiveFrom' | 'reason',
    value: string,
  ) => void
  onCheckChanges: (
    workTimeData?: Pick<
      ITechnician,
      'workDayFrom' | 'workDayTo' | 'lunchDuration'
    >,
  ) => void
  checkingChanges?: boolean
}

export default function SchedulePatternEditor({
  techData,
  location,
  isExpanded,
  isEditing,
  editingData,
  draftPattern,
  onToggleExpanded,
  onStartEditing,
  onCancelEditing,
  onToggleDayInDraft,
  onAddWeekToDraft,
  onRemoveWeekFromDraft,
  onUpdateEditingData,
  onCheckChanges,
  checkingChanges = false,
}: SchedulePatternEditorProps) {
  // Get work time values with inheritance logic: schedule pattern → location → defaults
  const getDisplayWorkTimes = () => {
    const schedulePattern = getActiveSchedulePattern(techData.schedulePatterns || [])
    
    // Start time: schedule pattern → location → 9:00 default
    let startTime = '9:00'
    if (location?.workDayFrom) {
      startTime = location.workDayFrom
    }
    if (schedulePattern?.workDayFrom) {
      startTime = schedulePattern.workDayFrom
    }

    // End time: schedule pattern → location → 6:00 PM default
    let endTime = '6:00 PM'
    if (location?.workDayTo) {
      endTime = location.workDayTo
    }
    if (schedulePattern?.workDayTo) {
      endTime = schedulePattern.workDayTo
    }

    // Lunch duration: schedule pattern → location → 30 min default
    let lunchDuration = 30
    if (location?.lunchDuration) {
      lunchDuration = location.lunchDuration
    }
    if (schedulePattern?.lunchDuration) {
      lunchDuration = schedulePattern.lunchDuration
    }

    return { startTime, endTime, lunchDuration }
  }

  const { startTime, endTime, lunchDuration } = getDisplayWorkTimes()

  // Work times editing state - use schedule pattern work times, not technician work times
  const schedulePattern = getActiveSchedulePattern(techData.schedulePatterns || [])
  const [useLocationWorkTimes, setUseLocationWorkTimes] = useState(
    !schedulePattern?.workDayFrom || !schedulePattern?.workDayTo,
  )
  const [useLocationLunch, setUseLocationLunch] = useState(
    !schedulePattern?.lunchDuration,
  )
  const [customStartTime, setCustomStartTime] = useState(
    schedulePattern?.workDayFrom || location?.workDayFrom || '09:00',
  )
  const [customEndTime, setCustomEndTime] = useState(
    schedulePattern?.workDayTo || location?.workDayTo || '18:00',
  )
  const [customLunchDuration, setCustomLunchDuration] = useState(
    schedulePattern?.lunchDuration || location?.lunchDuration || 30,
  )

  // Button state for save work times
  const [saveState, setSaveState] = useState<'normal' | 'loading' | 'success'>(
    'normal',
  )

  // Schedule pattern history modal state
  const [scheduleHistoryModalOpen, setScheduleHistoryModalOpen] = useState(false)

  // Validate work times - check if end time is before start time
  const hasTimeValidationError = () => {
    if (useLocationWorkTimes) return false // No validation needed when using location defaults
    
    if (!customStartTime || !customEndTime) return false // No validation if times not set
    
    // Convert time strings to minutes for comparison
    const startMinutes = parseInt(customStartTime.split(':')[0]) * 60 + parseInt(customStartTime.split(':')[1])
    const endMinutes = parseInt(customEndTime.split(':')[0]) * 60 + parseInt(customEndTime.split(':')[1])
    
    return endMinutes <= startMinutes
  }

  // Check if schedule pattern OR work times have changed
  const hasChanges = () => {
    // Check work times changes
    const currentWorkFrom = useLocationWorkTimes ? null : customStartTime
    const currentWorkTo = useLocationWorkTimes ? null : customEndTime
    const currentLunch = useLocationLunch ? null : customLunchDuration

    const workTimesChanged =
      currentWorkFrom !== schedulePattern?.workDayFrom ||
      currentWorkTo !== schedulePattern?.workDayTo ||
      currentLunch !== schedulePattern?.lunchDuration

    // Check if schedule pattern editing is happening (draftPattern exists)
    const schedulePatternChanged = draftPattern !== null

    return workTimesChanged || schedulePatternChanged
  }

  const handleCheckChanges = () => {
    // Only pass workTimeData if we have custom (non-location) values
    const hasCustomWorkTimes = !useLocationWorkTimes && customStartTime && customEndTime
    const hasCustomLunch = !useLocationLunch && customLunchDuration !== null

    if (hasCustomWorkTimes || hasCustomLunch) {
      const workTimeData: Partial<Pick<ITechnician, 'workDayFrom' | 'workDayTo' | 'lunchDuration'>> = {}
      
      if (hasCustomWorkTimes) {
        workTimeData.workDayFrom = customStartTime
        workTimeData.workDayTo = customEndTime
      }
      
      if (hasCustomLunch) {
        workTimeData.lunchDuration = customLunchDuration
      }

      onCheckChanges(workTimeData as Pick<ITechnician, 'workDayFrom' | 'workDayTo' | 'lunchDuration'>)
    } else {
      // Use location defaults - don't pass workTimeData parameter
      onCheckChanges()
    }
  }

  return (
    <>
      {/* Expanded Schedule Editor */}
      {isExpanded && (
        <div className={styles.scheduleEditorExpanded}>
          {!isEditing ? (
            // Read-only current schedule view
            <div className={styles.currentSchedule}>
              <div className={styles.scheduleHeader}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  <strong>Current Schedule:</strong>
                  <button
                    onClick={onStartEditing}
                    className={styles.scheduleLink}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Edit Schedule
                  </button>
                </div>
              </div>
              {schedulePattern ? (
                <div className={styles.scheduleDisplay}>
                  <div className={styles.scheduleContent}>
                    {[...schedulePattern.weekPatterns]
                      .sort((a, b) => a.week - b.week)
                      .map((week: WeekPattern, idx: number) => (
                        <div
                          key={idx}
                          className={styles.weekDisplay}
                        >
                          <span className={styles.weekNumber}>
                            W{week.week + 1}
                          </span>
                          {[
                            'sunday',
                            'monday',
                            'tuesday',
                            'wednesday',
                            'thursday',
                            'friday',
                            'saturday',
                          ].map((day) => (
                            <div
                              key={day}
                              className={`${styles.dayBox} ${
                                week.days.includes(day)
                                  ? styles.workDay
                                  : styles.offDay
                              }`}
                            >
                              {day.slice(0, 1).toUpperCase()}
                            </div>
                          ))}
                        </div>
                      ))}
                  </div>
                  <div className={styles.workTimeDisplay}>
                    <div className={styles.workTimeItem}>
                      <span className={styles.workTimeLabel}>Start:</span>
                      <span className={styles.workTimeValue}>{startTime}</span>
                    </div>
                    <div className={styles.workTimeItem}>
                      <span className={styles.workTimeLabel}>End:</span>
                      <span className={styles.workTimeValue}>{endTime}</span>
                    </div>
                    <div className={styles.workTimeItem}>
                      <span className={styles.workTimeLabel}>Lunch:</span>
                      <span className={styles.workTimeValue}>
                        {lunchDuration} min
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.noScheduleContainer}>
                  <span className={styles.noSchedule}>
                    No active schedule pattern
                  </span>
                  <button
                    onClick={onStartEditing}
                    className={styles.scheduleLink}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Create Schedule
                  </button>
                </div>
              )}
              
              {/* View Previous Schedules Button */}
              <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid var(--border-default)' }}>
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<HistoryIcon />}
                  onClick={() => setScheduleHistoryModalOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  View Previous Schedules
                </Button>
              </div>
            </div>
          ) : (
            // Interactive editing mode
            <div className={styles.editingSchedule}>
              <div className={styles.scheduleHeader}>
                <strong>Editing Schedule:</strong>
              </div>

              {/* Interactive Schedule Grid */}
              <div className={styles.interactiveGrid}>
                {draftPattern && draftPattern.weekPatterns &&
                  [...draftPattern.weekPatterns]
                    .sort((a, b) => a.week - b.week)
                    .map((week: WeekPattern, idx: number) => (
                      <div
                        key={idx}
                        className={styles.editableWeekRow}
                      >
                        <div className={styles.weekDisplay}>
                          <span className={styles.weekNumber}>
                            W{week.week + 1}
                          </span>
                          {[
                            'sunday',
                            'monday',
                            'tuesday',
                            'wednesday',
                            'thursday',
                            'friday',
                            'saturday',
                          ].map((day) => {
                            const isCurrentlyWorking =
                              schedulePattern?.weekPatterns
                                .find((w: WeekPattern) => w.week === week.week)
                                ?.days.includes(day.toLowerCase()) || false
                            const isDraftWorking = week.days.includes(
                              day.toLowerCase(),
                            )
                            const hasChanged =
                              isCurrentlyWorking !== isDraftWorking

                            return (
                              <div
                                key={day}
                                className={`${styles.dayBox} ${
                                  styles.clickableDay
                                } ${
                                  isDraftWorking
                                    ? styles.workDay
                                    : styles.offDay
                                } ${hasChanged ? styles.dayChanged : ''}`}
                                onClick={() =>
                                  onToggleDayInDraft(week.week, day)
                                }
                                title={
                                  hasChanged
                                    ? `${day}: ${
                                        isCurrentlyWorking ? 'Working' : 'Off'
                                      } → ${isDraftWorking ? 'Working' : 'Off'}`
                                    : `${day}: ${
                                        isDraftWorking ? 'Working' : 'Off'
                                      }`
                                }
                              >
                                {day.slice(0, 1).toUpperCase()}
                                {hasChanged && (
                                  <div className={styles.changeIndicator} />
                                )}
                              </div>
                            )
                          })}
                        </div>
                        {draftPattern.weekPatterns && draftPattern.weekPatterns.length > 1 && (
                          <IconButton
                            size='small'
                            className={styles.removeWeekButton}
                            onClick={() => onRemoveWeekFromDraft(week.week)}
                            title={`Remove Week ${week.week + 1}`}
                          >
                            <RemoveIcon />
                          </IconButton>
                        )}
                      </div>
                    ))}

                {/* Add Week Button */}
                <div className={styles.addWeekSection}>
                  <Button
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={onAddWeekToDraft}
                    variant='outlined'
                    className={styles.addWeekButton}
                  >
                    Add Week
                  </Button>
                </div>
              </div>

              {/* Work Times Editor Section */}
              <div style={{ marginTop: '24px' }}>
                <div className={styles.scheduleHeader}>
                  <strong>Work Times:</strong>
                </div>

                {/* Work Day Times Section */}
                <div className={styles.workTimeSection}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useLocationWorkTimes}
                        onChange={(e) =>
                          setUseLocationWorkTimes(e.target.checked)
                        }
                        color='primary'
                      />
                    }
                    label={`Use location default (${
                      location?.workDayFrom || '09:00'
                    } - ${location?.workDayTo || '18:00'})`}
                  />

                  {!useLocationWorkTimes && (
                    <div className={styles.workTimeInputs}>
                      <TextField
                        label='Start Time'
                        type='time'
                        value={customStartTime}
                        onChange={(e) => setCustomStartTime(e.target.value)}
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label='End Time'
                        type='time'
                        value={customEndTime}
                        onChange={(e) => setCustomEndTime(e.target.value)}
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                    </div>
                  )}
                </div>

                {/* Lunch Duration Section */}
                <div className={styles.workTimeSection}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useLocationLunch}
                        onChange={(e) => setUseLocationLunch(e.target.checked)}
                        color='primary'
                      />
                    }
                    label={`Use location default (${
                      location?.lunchDuration || 30
                    } minutes)`}
                  />

                  {!useLocationLunch && (
                    <div className={styles.workTimeInputs}>
                      <TextField
                        label='Lunch Duration (minutes)'
                        type='number'
                        value={customLunchDuration}
                        onChange={(e) =>
                          setCustomLunchDuration(parseInt(e.target.value))
                        }
                        size='small'
                        inputProps={{ min: 0, max: 240 }}
                        style={{ width: '200px' }}
                      />
                    </div>
                  )}
                </div>

                {/* Progressive Disclosure - Edit Controls */}
                {hasChanges() && (
                  <div
                    style={{
                      marginTop: '24px',
                      paddingTop: '16px',
                      borderTop: '1px solid #e0e0e0',
                    }}
                  >
                    <div className={styles.dateInputContainer}>
                      <TextField
                        label='Effective From'
                        type='date'
                        value={editingData?.effectiveFrom || ''}
                        onChange={(e) =>
                          onUpdateEditingData('effectiveFrom', e.target.value)
                        }
                        size='small'
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <EventIcon className={styles.effectiveDateInputIcon} />
                    </div>
                    <TextField
                      label='Reason for Change'
                      value={editingData?.reason || ''}
                      onChange={(e) =>
                        onUpdateEditingData('reason', e.target.value)
                      }
                      size='small'
                      placeholder='Why is this schedule being updated?'
                      fullWidth
                      style={{ marginTop: '12px' }}
                    />
                  </div>
                )}

                {/* Validation Error Message */}
                {hasTimeValidationError() && (
                  <div
                    style={{
                      color: '#d32f2f',
                      fontSize: '0.875rem',
                      marginTop: '12px',
                      padding: '8px 12px',
                      backgroundColor: '#ffebee',
                      borderRadius: '4px',
                      border: '1px solid #ffcdd2'
                    }}
                  >
                    End time must be after start time
                  </div>
                )}

                {/* View Previous Schedules Button */}
                <div
                  style={{
                    marginTop: '24px',
                    paddingTop: '16px',
                    borderTop: '1px solid #e0e0e0',
                  }}
                >
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<HistoryIcon />}
                    onClick={() => setScheduleHistoryModalOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    View Previous Schedules
                  </Button>
                </div>

                {/* Check Changes and Cancel Buttons */}
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end',
                    marginTop: '16px',
                    paddingTop: '12px',
                    borderTop: '1px solid #e0e0e0',
                  }}
                >
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={onCancelEditing}
                  >
                    Cancel
                  </Button>
                  <Button
                    size='small'
                    variant='contained'
                    disabled={!editingData?.reason?.trim() || !hasChanges() || checkingChanges || hasTimeValidationError()}
                    onClick={handleCheckChanges}
                  >
                    {checkingChanges ? 'Checking...' : 'Check Changes'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Schedule Pattern History Modal */}
      <SchedulePatternHistoryModal
        open={scheduleHistoryModalOpen}
        onClose={() => setScheduleHistoryModalOpen(false)}
        technicianObjectId={techData.objectId}
        technicianName={techData.name || 'Unknown Technician'}
      />
    </>
  )
}
